import React from 'react';
import BackRow from 'components/BackRow/BackRow';
import Card from 'components/Card/Card';
import { EditText, EditTextArea, EditDropDown } from 'components/EditText/EditText';
import { MessageDialog } from 'components/Dialog/Dialog';
import EditBrowse from 'components/EditBrowse/EditBrowse';
import { get, post, upload } from 'lib/comms';
import { getLanguages } from 'lib/languages';
import moment from 'moment';
import { setData, getData } from '../../../../lib/data';
import OrgFlags from './OrgFlags';
import OrgTheme from './OrgTheme';
import OrgCustomApp from './OrgCustomApp';

import './OrgPage.css';

export default class GroupPage extends React.Component {
    constructor(props) {
        super(props);
        let { organisation = {} } = props;
        const isNew = organisation.id === 'new';
        const supportActive =
            organisation.support_window_expires_at && new Date(organisation.support_window_expires_at) > new Date();
        this.state = {
            organisation,
            isNew,
            editing: [isNew],
            showMessage: null,
            supportActive,
            orgFlags: [],
            fileContent: '',
            theme: [],
            files: [],
        };
    }

    componentDidMount() {
        this.getOrganisationData();
    }

    async getOrganisationData() {
        let orgID = this.state.organisation.id;

        if (orgID && orgID !== 'new') {
            const featureFlagsResp = await get(`cms/feature_flags/${orgID}`, {}, true);
            const orgFlags = featureFlagsResp && featureFlagsResp.feature_flags ? featureFlagsResp.feature_flags : [];

            const themeResp = await get('cms/org_theme/', { organisation_id: orgID }, true);
            const theme = themeResp && themeResp.theme ? themeResp.theme : [];

            const trustHandBooksResp = await get(`cms/trust_handbook/organisation/${orgID}`, {}, true);

            const customAppResp = await get('cms/custom_app', { organisation_id: orgID }, true);
            const customApp = customAppResp && customAppResp.customApp ? customAppResp.customApp : {};

            this.setState({ orgFlags: [orgFlags], theme, customApp, files: trustHandBooksResp.data });
        }
    }

    getLanguageOptions = () => {
        const options = getLanguages();

        options.unshift({
            id: 'select',
            name: 'Select',
        });

        return options;
    };

    setEditing = async (index) => {
        let { editing, organisation } = this.state;
        if (editing[index]) {
            // save to db
            if (!organisation.company_name) return;
            const resp = await post('cms/organisations', { organisation }, true);
            if (resp && resp.success) {
                editing[index] = false;
                if (resp.id) this.setState({ organisation: { ...organisation, id: resp.id }, isNew: false });
            } else console.log(resp);
            if (resp.message) {
                setTimeout(() => this.setState({ showMessage: { message: resp.message, title: 'Save error' } }), 500);
            }
        } else editing[index] = true;
        this.setState({ editing });
    };

    setFileContent = async (index) => {
        let { editing, organisation, fileContent } = this.state;
        if (editing[index]) {
            // save to db
            if (!organisation.id) return;
            const resp = await post(
                `cms/organisation_config/${organisation.id}/firebase_service`,
                { value: fileContent },
                true,
            );
            if (resp && resp.success) {
                editing[index] = false;
            } else console.log(resp);
            if (resp.message) {
                setTimeout(() => this.setState({ showMessage: { message: resp.message, title: 'Save error' } }), 500);
            }
        } else editing[index] = true;
        this.setState({ editing });
    };

    setFlag = (flagId, flagValue) => {
        let { orgFlags } = this.state;
        orgFlags[0].forEach((flag) => {
            if (flag.id === flagId) {
                flag.flag_value = flagValue;
            }
        });
        this.setState({ orgFlags: orgFlags });
    };

    setCApp = (customAppKey, customAppValue) => {
        let { customApp } = this.state;
        customApp[customAppKey] = customAppValue;
        this.setState({ customApp: customApp });
    };

    setFeatureFlags = async (index) => {
        let { orgFlags, organisation, editing } = this.state;
        if (editing[index]) {
            // save to db
            if (!organisation.id) return;
            orgFlags[0].forEach(async (flag) => {
                const resp = await post(
                    `cms/feature_flags/${flag.id}/?organisation_id=${organisation.id}`,
                    { flag_value: flag.flag_value ? 'true' : false },
                    true,
                );
                if (resp.message) {
                    setTimeout(
                        () => this.setState({ showMessage: { message: resp.message, title: 'Save error' } }),
                        500,
                    );
                }
            });
            editing[index] = false;
        } else editing[index] = true;
        this.setState({ editing });
    };

    setCustomApp = async (index) => {
        let { customApp, organisation, editing } = this.state;
        if (editing[index]) {
            // save to db
            if (!organisation.id) return;
            if (!customApp.id) {
                customApp.id = organisation.company_name;
            }
            customApp.created_at = undefined;
            customApp.organisation_id = organisation.id;
            const resp = await post('cms/custom_app', customApp, true);
            if (resp.message) {
                setTimeout(() => this.setState({ showMessage: { message: resp.message, title: 'Save error' } }), 500);
            }
            editing[index] = false;
        } else editing[index] = true;
        this.setState({ editing });
    };

    setTrustHandBook = async (index) => {
        let { organisation, editing, files } = this.state;

        if (editing[index]) {
            if (!files.length) {
                return this.setState({
                    showMessage: { message: 'Please include a valid file', title: 'Trust handbook' },
                });
            }

            for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
                const file = files[fileIndex];

                if (!file.languageCode || !file.original_name) {
                    return this.setState({
                        showMessage: {
                            message: 'Please make sure you add a language code and a valid file',
                            title: 'Trust handbook',
                        },
                    });
                }
            }

            const languages = getLanguages();
            const languageCodeIds = files.map(
                (item) => languages.filter((language) => language.id === item.languageCode)[0].name,
            );

            const duplicates = [...new Set(languageCodeIds.filter((e, i, a) => a.indexOf(e) !== i))];

            if (duplicates.length) {
                return this.setState({
                    showMessage: {
                        message: `The following language codes are duplicate ${duplicates.join('')}.`,
                        title: 'Trust handbook',
                    },
                });
            }
        }

        const fileIds = await this.uploadFiles();
        if (editing[index]) {
            // save to db
            if (!organisation.id) return;
            await post(`cms/trust_handbook/${organisation.id}`, { filesItem: fileIds }, true);
            editing[index] = false;

            const newFiles = [...this.state.files];

            for (index = 0; index < fileIds.length; index++) {
                if (newFiles[index] && !newFiles[index].hasOwnProperty('id')) {
                    newFiles[index].id = fileIds[index].fileId;
                }
            }

            this.setState({
                files: newFiles,
            });
        } else editing[index] = true;
        this.setState({ editing });
    };

    setThemeVal = (themeId, themeVal) => {
        let theme = this.state.theme.map((item) => {
            if (item.id === themeId) {
                return {
                    ...item,
                    value: themeVal,
                };
            }
            return item;
        });
        this.setState({ theme });
    };

    setOrgTheme = async (index) => {
        let { theme, organisation, editing } = this.state;
        if (editing[index]) {
            // save to db
            if (!organisation.id) return;
            theme.forEach(async ({ id, value }) => {
                const resp = await post(
                    `cms/org_theme/${id}/?organisation_id=${organisation.id}`,
                    { theme_value: value },
                    true,
                );
                if (resp.message) {
                    setTimeout(
                        () => this.setState({ showMessage: { message: resp.message, title: 'Save error' } }),
                        500,
                    );
                }
            });
            editing[index] = false;
        } else editing[index] = true;
        this.setState({ editing });
    };

    requestSupportToken = async () => {
        const tokenRequest = await post(`cms/support/join/${this.state.organisation.id}`, {}, true);

        if (tokenRequest && tokenRequest.token) {
            // save existing token in another cookie as a backup so we can return from support mode
            setData('token_admin', getData('token', null), true);
            setData('token', tokenRequest.token, true);
            window.location = '/';
        } else {
            this.setState({
                showMessage: {
                    message: `Couldn't active remote support for ${this.state.organisation.company_name}.`,
                },
            });
        }
    };

    enforceMinMax(el, min, max) {
        if (el !== '') {
            if (Number(el) < parseInt(min)) {
                el = min;
            }
            if (Number(el) > parseInt(max)) {
                el = max;
            }
        }
        return el;
    }

    onFileChange = (file, index) => {
        let files = [...this.state.files];

        files[index] = {
            ...files[index],
            file: file,
            original_name: file.name,
        };

        this.setState({
            files: files,
        });
    };

    onClickAddFiles = () => {
        let files = [
            ...this.state.files,
            {
                languageCode: '',
                file: '',
            },
        ];

        this.setState({
            files: files,
        });
    };

    removeFile = (index) => {
        const files = [...this.state.files];
        files.splice(index, 1);

        this.setState({
            files: files,
        });
    };

    onChangeFileUrl = (index, value) => {
        let files = [...this.state.files];

        if (value === 'select') {
            value = null;
        }

        files[index] = {
            ...files[index],
            languageCode: value,
        };

        this.setState({
            files: files,
        });
    };

    // upload files item to backend before update/create of  banner item
    uploadFiles = async () => {
        const { files, organisation } = this.state;
        let fileIds = [];
        const fileCount = files.length;
        for (let index = 0; index < fileCount; index++) {
            const fileItem = files[index];
            if (!fileItem.id) {
                const fileResp = await upload(
                    `cms/upload_file/${fileItem.original_name}/trust_handbook`,
                    'file',
                    fileItem.file,
                    true,
                    organisation.id,
                );

                if (fileResp && fileResp['file_id']) {
                    fileIds.push({
                        fileId: fileResp['file_id'],
                        fileLanguageCode: fileItem.languageCode,
                    });
                }
            } else {
                fileIds.push({
                    fileId: fileItem.id,
                    fileLanguageCode: fileItem.languageCode,
                });
            }
        }

        return fileIds;
    };

    onViewFile = async (file) => {
        if (file.file) {
            // We have just added the file create a FileReader to be able to load the document
            const reader = new FileReader();
            reader.onload = (reader) => {
                let blob = reader.target.result;
                let file = new Blob([blob], { type: 'application/pdf' });
                let fileURL = window.URL.createObjectURL(file);
                window.open(fileURL);
            };

            reader.readAsArrayBuffer(file.file);
        } else {
            let { organisation } = this.state;
            const trustHandBookFileResp = await get(
                `cms/trust_handbook/${organisation.id}/file/${file.file_id}`,
                {},
                true,
            );

            window.open(trustHandBookFileResp.data);
        }
    };

    render() {
        let { orgFlags, organisation, editing, isNew, supportActive, fileContent, theme, files, customApp } = this.state;
        return (
            <div className="maincontainer">
                <BackRow onBack={this.props.onBack} />

                <Card
                    title="Organisation information"
                    editing={editing[0] ? '1' : undefined}
                    onToggle={() => this.setEditing(0)}
                >
                    {organisation.member_count ? (
                        <p className="mt-2 mb-2" style={{ color: '#717171' }}>
                            Members: {organisation.member_count}
                        </p>
                    ) : null}
                    <EditText
                        label="Organisation Name"
                        editing={editing[0] ? '1' : undefined}
                        onChange={(text) => this.setState({ organisation: { ...organisation, company_name: text } })}
                        value={organisation.company_name}
                    />
                    <EditText
                        type="number"
                        label="Organisation Latitude"
                        editing={editing[0] ? '1' : undefined}
                        onChange={(text) => {
                            this.setState({
                                organisation: { ...organisation, latitude: this.enforceMinMax(text, -90, 90) },
                            });
                        }}
                        value={organisation.latitude}
                    />
                    <EditText
                        type="number"
                        label="Organisation Longitude"
                        editing={editing[0] ? '1' : undefined}
                        onChange={(text) => {
                            this.setState({
                                organisation: { ...organisation, longitude: this.enforceMinMax(text, -180, 180) },
                            });
                        }}
                        value={organisation.longitude}
                    />
                    <EditText
                        className="mt-3"
                        label="Points Cap"
                        editing={editing[0] ? '1' : undefined}
                        onChange={(text) =>
                            !isNaN(text) &&
                            this.setState({ organisation: { ...organisation, points_cap: Math.round(text) } })
                        }
                        value={organisation.points_cap}
                    />
                    <EditText
                        label="Intranet URL"
                        editing={editing[0] ? '1' : undefined}
                        onChange={(text) => this.setState({ organisation: { ...organisation, intranet_link: text } })}
                        value={organisation.intranet_link}
                    />
                    <EditText
                        label="Azure Client Id"
                        editing={editing[0] ? '1' : undefined}
                        onChange={(text) => this.setState({ organisation: { ...organisation, azure_client_id: text } })}
                        value={organisation.azure_client_id}
                    />
                    <EditText
                        label="Azure Tenant Id"
                        editing={editing[0] ? '1' : undefined}
                        onChange={(text) => this.setState({ organisation: { ...organisation, azure_tenant_id: text } })}
                        value={organisation.azure_tenant_id}
                    />
                    <EditText label="Azure Redirect Url" editing={undefined} value={'com.engage4://auth/'} />
                    <p
                        style={{
                            color: '#af365fd0',
                            fontWeight: 600,
                            fontSize: 13,
                            paddingLeft: 4,
                            paddingTop: 0,
                            margin: 0,
                        }}
                    >
                        Please add this url into Azure Portal.
                    </p>
                    {isNew ? (
                        <EditText
                            className="mt-3"
                            label="Admin login (email)"
                            editing={editing[0] ? '1' : undefined}
                            helpText="Add a username so that they can log in"
                            onChange={(text) => this.setState({ organisation: { ...organisation, email: text } })}
                            value={organisation.email}
                        />
                    ) : null}
                    {isNew ? (
                        <EditText
                            className="mt-1"
                            label="Admin password"
                            editing={editing[0] ? '1' : undefined}
                            helpText="Leave blank for magic link login"
                            type="password"
                            onChange={(text) => this.setState({ organisation: { ...organisation, password: text } })}
                            value={organisation.password}
                        />
                    ) : null}
                </Card>
                <OrgTheme
                    setTheme={this.setThemeVal}
                    theme={theme}
                    isEditing={editing[1] ? '1' : undefined}
                    onToggle={() => this.setOrgTheme(1)}
                    organisation={organisation.id}
                />
                <OrgFlags
                    setFlag={this.setFlag}
                    flags={orgFlags}
                    isEditing={editing[2] ? '1' : undefined}
                    onToggle={() => this.setFeatureFlags(2)}
                    organisation={organisation.id}
                />
                <OrgCustomApp
                    customApp={customApp}
                    isEditing={editing[4] ? '1' : undefined}
                    onToggle={() => this.setCustomApp(4)}
                    setCustomApp={this.setCApp}
                />
                <Card
                    title="File Content"
                    editing={editing[2] ? '1' : undefined}
                    onToggle={() => this.setFileContent(2)}
                >
                    <div className="mt-2">
                        <EditTextArea
                            label="Firebase Service JSON Value"
                            editing={editing[2] ? '1' : undefined}
                            onChange={(text) => this.setState({ fileContent: text })}
                            value={fileContent}
                        />
                    </div>
                </Card>

                <Card
                    title="Trust Handbook"
                    editing={editing[5] ? '1' : undefined}
                    onToggle={() => this.setTrustHandBook(5)}
                >
                    {files.map((file, index) => (
                        <div className="fileContainer">
                            <EditDropDown
                                className="poll-duration"
                                label="language"
                                options={this.getLanguageOptions()}
                                editing={editing[5] && !file.id}
                                onChange={(value) => this.onChangeFileUrl(index, value)}
                                value={file.languageCode}
                            />
                            <div style={{ width: '20px' }} />
                            <EditBrowse
                                editing={editing[5] && !file.id}
                                onFileChange={(slctdFile) => this.onFileChange(slctdFile, index)}
                                fileName={file['original_name']}
                                acceptFileTypes={'application/pdf'}
                                fileSizeLimit={10000000}
                                fileSizeLimiteMessage={'You can upload maximum of 10MB file'}
                            />
                            <div className="fileContainer-inner">
                                {file.original_name ? (
                                    <button
                                        className="btn text-button"
                                        onClick={() => this.onViewFile(file)}
                                        disabled={!file.original_name}
                                        isEditing={editing[5] && !file.id}
                                    >
                                        View
                                    </button>
                                ) : null}
                                <button
                                    className="btn btn-remove"
                                    onClick={() => this.removeFile(index)}
                                    disabled={!editing[5]}
                                    isEditing={editing[5] && !file.id}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                    <div className="bottomFileContainer">
                        <button
                            className="btn btn-primary knowledgebase-btn"
                            disabled={!editing[5]}
                            onClick={this.onClickAddFiles}
                        >
                            ADD ANOTHER LANGUAGE
                        </button>
                    </div>
                </Card>

                {!isNew && (
                    <Card title="Support">
                        <div className="mt-2">
                            {supportActive ? (
                                <div>
                                    <button className="btn btn-primary" onClick={this.requestSupportToken}>
                                        REMOTE SUPPORT
                                    </button>
                                    <br />
                                    <div className="mt-2">
                                        Remote support expires{' '}
                                        <b>{moment(organisation.support_window_expires_at).fromNow()}</b> (
                                        {moment(organisation.support_window_expires_at).format(
                                            'dddd, MMMM Do YYYY, h:mm:ssa',
                                        )}
                                        )
                                    </div>
                                </div>
                            ) : (
                                <p>Organisation has not activated remote support.</p>
                            )}
                        </div>
                    </Card>
                )}

                {this.state.showMessage ? (
                    <MessageDialog
                        message={this.state.showMessage.message}
                        title={this.state.showMessage.title}
                        onClose={() => this.setState({ showMessage: null })}
                    />
                ) : null}
            </div>
        );
    }
}
